import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { copy } from '../../config/copy';
import type { FollowRow } from './types';
import { useDocumentTitle } from '../../lib/title';

interface Page {
  items: FollowRow[];
  next_cursor: string | null;
}

/**
 * `/{username}/followers` and `/{username}/following`.
 *
 * Cursor-paginated, matching the API (§7). `useInfiniteQuery` maps onto that
 * directly - `next_cursor` becomes the next page param, and a null one is what
 * ends the list.
 *
 * Explicitly *not* offset pagination: a follower list of an active account is
 * appended to while it is being read, which makes offsets duplicate and skip
 * rows visibly.
 */
export function FollowListPage() {
  const { username = '' } = useParams();
  const { pathname } = useLocation();
  const direction = pathname.endsWith('/following') ? 'following' : 'followers';

  useDocumentTitle(username ? `@${username} · ${direction}` : null);

  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['follows', username, direction],
    queryFn: ({ pageParam }) =>
      api.get<Page>(`/users/${encodeURIComponent(username)}/${direction}`, {
        query: { cursor: pageParam ?? undefined, limit: 30 },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });

  const rows = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-5 px-6 py-10">
      <div>
        <Link to={`/${username}`} className="text-sm text-soft hover:text-ink">
          ← @{username}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {direction === 'following' ? 'Following' : copy['noun.follower.plural']}
        </h1>
      </div>

      {isPending && <p className="text-soft">Loading…</p>}
      {error && <Alert tone="error">Could not load that list.</Alert>}

      {data && rows.length === 0 && (
        <Alert tone="info">
          {direction === 'following' ? 'Not following anybody yet.' : 'No one yet.'}
        </Alert>
      )}

      {rows.length > 0 && (
        <Card className="divide-y divide-line">
          {rows.map((row) => (
            <Link
              key={row.id}
              to={`/${row.username}`}
              className="flex items-center gap-3 p-4 hover:bg-raised"
            >
              <Avatar src={row.avatar} name={row.display_name} size={40} />
              <div className="min-w-0">
                <div className="truncate font-medium">{row.display_name}</div>
                <div className="truncate text-sm text-faint">@{row.username}</div>
              </div>
            </Link>
          ))}
        </Card>
      )}

      {hasNextPage && (
        <Button variant="secondary" loading={isFetchingNextPage} onClick={() => void fetchNextPage()}>
          Load more
        </Button>
      )}
    </div>
  );
}
